import { createRouter, createWebHistory } from 'vue-router';
import { PageBootstrapKind, PermissionKind, type GetPageBootstrapQuery } from './api/generated';
import SettingsLayout from './components/settings/SettingsLayout.vue';
import { getLastSessionUserId, useSessionStore } from './stores/session';
import { useWorkspaceStore } from './stores/workspace';
import { getEditorTabsState } from './stores/workspaceViewport';
import AdminProjectsView from './views/AdminProjectsView.vue';
import AdminRolesView from './views/AdminRolesView.vue';
import AdminSettingsView from './views/AdminSettingsView.vue';
import AdminUserEditView from './views/AdminUserEditView.vue';
import AdminUsersView from './views/AdminUsersView.vue';
import AppearanceView from './views/AppearanceView.vue';
import ChangesView from './views/ChangesView.vue';
import ConfirmEmailChangeView from './views/ConfirmEmailChangeView.vue';
import ForgotPasswordView from './views/ForgotPasswordView.vue';
import HomeView from './views/HomeView.vue';
import LoginView from './views/LoginView.vue';
import MaintenanceView from './views/MaintenanceView.vue';
import NotFoundView from './views/NotFoundView.vue';
import ProjectAccessView from './views/ProjectAccessView.vue';
import RegisterView from './views/RegisterView.vue';
import ResetPasswordView from './views/ResetPasswordView.vue';
import SettingsView from './views/SettingsView.vue';
import VerifyEmailView from './views/VerifyEmailView.vue';

const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/', component: HomeView, meta: { requiresAuth: true } },
    { path: '/documents/:documentId(\\d+)', name: 'workspace-document', component: HomeView, meta: { requiresAuth: true } },
    { path: '/changes', component: ChangesView, meta: { requiresAuth: true, chrome: 'changes' } },
    { path: '/login', component: LoginView },
    { path: '/register', component: RegisterView },
    { path: '/forgot-password', component: ForgotPasswordView },
    { path: '/reset-password', component: ResetPasswordView },
    { path: '/verify-email', component: VerifyEmailView },
    { path: '/confirm-email-change', component: ConfirmEmailChangeView },
    {
      path: '/settings',
      component: SettingsLayout,
      meta: { requiresAuth: true, chrome: 'settings' },
      children: [
        { path: '', redirect: '/settings/profile' },
        { path: 'profile', component: SettingsView },
        { path: 'appearance', component: AppearanceView },
        {
          path: 'general',
          component: AdminSettingsView,
          meta: { requiredGlobalPermission: PermissionKind.MANAGE_SETTINGS }
        },
        {
          path: 'projects',
          component: AdminProjectsView,
          meta: { requiredGlobalPermission: [PermissionKind.CREATE_PROJECT, PermissionKind.MANAGE_PROJECT_ACCESS] }
        },
        {
          path: 'projects/:projectId/access',
          component: ProjectAccessView,
          meta: { requiredGlobalPermission: PermissionKind.MANAGE_PROJECT_ACCESS }
        },
        {
          path: 'users',
          component: AdminUsersView,
          meta: { requiredGlobalPermission: PermissionKind.MANAGE_USERS }
        },
        {
          path: 'users/:userId',
          component: AdminUserEditView,
          meta: { requiredGlobalPermission: PermissionKind.MANAGE_USERS }
        },
        {
          path: 'roles',
          component: AdminRolesView,
          meta: { requiredGlobalPermission: PermissionKind.MANAGE_ROLES }
        },
        {
          path: 'maintenance',
          component: MaintenanceView,
          meta: { requiredGlobalPermission: PermissionKind.ADMIN_ACCESS }
        }
      ]
    },
    { path: '/admin', redirect: '/settings/projects' },
    { path: '/admin/settings', redirect: '/settings/general' },
    { path: '/admin/users', redirect: '/settings/users' },
    { path: '/admin/roles', redirect: '/settings/roles' },
    {
      path: '/admin/projects/:projectId/access',
      redirect: to => `/settings/projects/${to.params.projectId}/access`
    },
    { path: '/:pathMatch(.*)*', component: NotFoundView }
  ]
});

router.beforeEach(async to => {
  const session = useSessionStore();
  const request = createBootstrapRequest(to);
  try {
    if (!canUseLocalWorkspaceBootstrap(request)) {
      await session.bootstrapPage(request, `${to.fullPath}:${JSON.stringify(request)}`);
    }
  } catch {
    return to.meta.requiresAuth ? { path: '/login', query: { returnUrl: to.fullPath } } : true;
  }

  if (to.meta.requiresAuth && !session.isAuthenticated) {
    return { path: '/login', query: { returnUrl: to.fullPath } };
  }

  const requiredGlobalPermission = to.meta.requiredGlobalPermission as PermissionKind | PermissionKind[] | undefined;
  if (requiredGlobalPermission != null && !session.hasAnyGlobalPermission(requiredGlobalPermission)) {
    return { path: '/' };
  }

  return true;
});

function canUseLocalWorkspaceBootstrap(request: GetPageBootstrapQuery): boolean {
  if (request.pageKind !== PageBootstrapKind.WORKSPACE) {
    return false;
  }

  const workspace = useWorkspaceStore();
  return workspace.workspace != null;
}

function createBootstrapRequest(to: { path: string; query: Record<string, unknown>; params: Record<string, unknown> }): GetPageBootstrapQuery {
  const documentId = getRouteId(to.params.documentId);
  if (to.path === '/' || documentId != null) {
    return {
      pageKind: PageBootstrapKind.WORKSPACE,
      documentId,
      workspaceDocumentIds: getSavedWorkspaceDocumentIds(documentId)
    };
  }

  if (to.path === '/changes') {
    return {
      pageKind: PageBootstrapKind.CHANGES,
      timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
      limitDays: 14
    };
  }

  if (to.path === '/settings/profile') {
    return { pageKind: PageBootstrapKind.PROFILE };
  }

  if (to.path === '/settings/appearance') {
    return { pageKind: PageBootstrapKind.APPEARANCE };
  }

  if (to.path === '/settings/general') {
    return { pageKind: PageBootstrapKind.ADMIN_SETTINGS };
  }

  if (to.path === '/settings/projects') {
    return { pageKind: PageBootstrapKind.ADMIN_PROJECTS };
  }

  if (to.path.startsWith('/settings/projects/') && to.path.endsWith('/access')) {
    return {
      pageKind: PageBootstrapKind.PROJECT_ACCESS,
      projectId: getRouteId(to.params.projectId)
    };
  }

  if (to.path === '/settings/users') {
    return {
      pageKind: PageBootstrapKind.ADMIN_USERS,
      page: 1,
      pageSize: 20
    };
  }

  if (to.path.startsWith('/settings/users/')) {
    return {
      pageKind: PageBootstrapKind.ADMIN_USER,
      userId: getRouteId(to.params.userId)
    };
  }

  if (to.path === '/settings/roles') {
    return { pageKind: PageBootstrapKind.ADMIN_ROLES };
  }

  if (to.path === '/settings/maintenance') {
    return { pageKind: PageBootstrapKind.MAINTENANCE };
  }

  return { pageKind: PageBootstrapKind.PUBLIC };
}

function getSavedWorkspaceDocumentIds(routeDocumentId: string | undefined): string[] {
  const userId = getLastSessionUserId();
  if (userId == null) {
    return [];
  }

  const ids = new Set<string>();
  for (const pane of getEditorTabsState(userId)?.panes ?? []) {
    for (const tab of pane.tabs) {
      ids.add(tab.documentId);
    }
  }

  if (routeDocumentId != null) {
    ids.delete(routeDocumentId);
  }

  return [...ids];
}

function getRouteId(value: unknown): string | undefined {
  const candidate = Array.isArray(value) ? value[0] : value;
  return typeof candidate === 'string' && /^\d+$/.test(candidate) ? candidate : undefined;
}

export default router;
